import { useCallback } from 'react';
import { usePlan } from '../contexts/PlanContext';
import { useUpgradePrompt } from '../contexts/UpgradePromptContext';
import { FeatureKey, PlanTier, getFeatureDefinition } from '../business/billing/featureCatalog';

export interface FeatureAccess {
  /** True when the current plan unlocks the feature. */
  available: boolean;
  /** True while plan data is loading — render an enabled state to avoid lock flashes. */
  loading: boolean;
  /** Lowest tier that includes this feature. */
  requiredTier: PlanTier;
  /** The tenant's current tier, or null if loading / no subscription. */
  currentTier: PlanTier | null;
  /** Open the upgrade modal targeted at this feature. */
  requestUpgrade: () => void;
  /**
   * Convenience: wraps an onClick handler so that locked features open the upgrade
   * modal instead of running the action. Returns a function suitable for `onClick`.
   *
   *   <button onClick={guard(() => doThing())}>Do thing</button>
   */
  guard: <T extends (...args: any[]) => any>(onAllowed: T) => (...args: Parameters<T>) => void;
}

/**
 * Primary hook for plan-aware UI. Lookup is keyed off `FEATURE_CATALOG`.
 *
 *   const { available, requestUpgrade, guard } = useFeatureAccess('SmartFill');
 *
 *   <button
 *     onClick={guard(() => runSmartFill())}
 *     className={available ? '' : 'opacity-60'}
 *   >
 *     Smart fill
 *   </button>
 */
export function useFeatureAccess(feature: FeatureKey | string): FeatureAccess {
  const { tier, loading, hasFeature } = usePlan();
  const { showUpgrade } = useUpgradePrompt();
  const def = getFeatureDefinition(feature);

  const available = hasFeature(feature);

  const requestUpgrade = useCallback(() => {
    showUpgrade({
      reason: 'feature',
      feature,
      currentTier: tier,
    });
  }, [showUpgrade, feature, tier]);

  const guard = useCallback(
    <T extends (...args: any[]) => any>(onAllowed: T) =>
      (...args: Parameters<T>) => {
        if (!available) {
          requestUpgrade();
          return;
        }
        onAllowed(...args);
      },
    [available, requestUpgrade]
  );

  return {
    available,
    loading,
    requiredTier: def.requiredTier,
    currentTier: tier,
    requestUpgrade,
    guard,
  };
}
